import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import type { ComboboxOption } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { usePermission } from '@/components/guards/permission-guard';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { CariTypeModal, type CariTypeForEdit } from '@/components/trading/cari/cari-type-modal';
import { CariTypeDetailPanel } from '@/components/trading/cari/cari-type-detail-panel';
import { CariCreateModals } from '@/components/trading/cari/cari-create-modals';
import { cariTypeLabel } from '@/components/trading/cari/cari-type-display';
import { cn } from '@/lib/utils';

function accountLabel(account: { code: string; name: string } | null): string {
  return account ? `${account.code} · ${account.name}` : '—';
}

// Admin management for cari types: registry CRUD, per-type accounting accounts
// (muhasebe/gelir/gider), the assignable account pool, and per-cari account
// assignment.
export function CariTypesPage() {
  const { t, i18n } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const canCreate = usePermission('ACCOUNTING', 'CREATE', 'TRADING');
  const canEdit = usePermission('ACCOUNTING', 'EDIT', 'TRADING');
  const canDelete = usePermission('ACCOUNTING', 'DELETE', 'TRADING');
  const canCreateCustomer = usePermission('CUSTOMERS', 'CREATE', 'TRADING');
  const canCreateSupplier = usePermission('SUPPLIERS', 'CREATE', 'TRADING');
  const canAddCari = (code: string) => code === 'T' ? canCreateSupplier : canCreateCustomer;

  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<CariTypeForEdit | null>(null);
  const [typeToDelete, setTypeToDelete] = useState<{ id: string; name: string } | null>(null);
  const [createCariType, setCreateCariType] = useState<{ code: string; label: string } | null>(null);

  const utils = trpc.useUtils();
  const { data: types, isLoading } = trpc.tradingCariType.listDetailed.useQuery();
  const { data: accountData } = trpc.tradingAccount.list.useQuery({
    page: 1,
    limit: 2000,
    isActive: true,
  });

  const accountOptions: ComboboxOption[] = useMemo(
    () =>
      (accountData?.accounts ?? []).map((a) => ({
        value: a.id,
        label: `${a.code} · ${a.name}`,
        keywords: [a.code, a.name],
      })),
    [accountData]
  );

  const deleteMutation = trpc.tradingCariType.delete.useMutation({
    onSuccess: () => {
      utils.tradingCariType.listDetailed.invalidate();
      utils.tradingCariType.list.invalidate();
      showSuccessToast(t('cariTypes.typeDeleted'));
      setTypeToDelete(null);
    },
    onError: (error) => showErrorToast(error.message),
  });

  const selectedType = types?.find((tp) => tp.id === selectedTypeId) ?? null;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('cariTypes.title')}</h1>
          <p className="text-muted-foreground">{t('cariTypes.subtitle')}</p>
        </div>
        {canCreate && (
          <Button
            onClick={() => {
              setEditingType(null);
              setModalOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('cariTypes.newType')}
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Types table */}
      {!isLoading && types && (
        <div className="overflow-x-auto border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-2.5 font-medium">{t('cariTypes.code')}</th>
                <th className="px-4 py-2.5 font-medium">{t('cariTypes.name')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('cariTypes.cariCount')}</th>
                <th className="px-4 py-2.5 font-medium">{t('cariTypes.muhasebeAccount')}</th>
                <th className="px-4 py-2.5 font-medium">{t('cariTypes.gelirAccount')}</th>
                <th className="px-4 py-2.5 font-medium">{t('cariTypes.giderAccount')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('cariTypes.accountPool')}</th>
                <th className="px-4 py-2.5 font-medium">{tCommon('status')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {types.map((type) => (
                <tr
                  key={type.id}
                  onClick={() => setSelectedTypeId(type.id === selectedTypeId ? null : type.id)}
                  className={cn(
                    'cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/50',
                    type.id === selectedTypeId && 'bg-muted/70'
                  )}
                >
                  <td className="px-4 py-2.5 font-semibold">{type.code}</td>
                  <td className="px-4 py-2.5">
                    {type.name}
                    {type.isSystem && (
                      <span className="ml-2 rounded-md bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                        {t('cariTypes.system')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{type.cariCount}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{accountLabel(type.muhasebeAccount)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{accountLabel(type.gelirAccount)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{accountLabel(type.giderAccount)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{type.assignedAccounts.length}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className={cn(
                        'rounded-md px-1.5 py-0.5 text-xs',
                        type.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {type.isActive ? tCommon('active') : tCommon('inactive')}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {canAddCari(type.code) && type.isActive && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCreateCariType({
                              code: type.code,
                              label: cariTypeLabel(type, i18n.language),
                            });
                          }}
                          className="p-1.5 text-muted-foreground hover:text-primary"
                          title={t('cariTypes.addCari')}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingType(type);
                            setModalOpen(true);
                          }}
                          className="p-1.5 text-muted-foreground hover:text-primary"
                          title={tCommon('edit')}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canDelete && !type.isSystem && type.cariCount === 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTypeToDelete({ id: type.id, name: type.name });
                          }}
                          className="p-1.5 text-muted-foreground hover:text-red-600"
                          title={tCommon('delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Defaults note */}
      {!isLoading && (
        <p className="text-xs text-muted-foreground">{t('cariTypes.defaultsNote')}</p>
      )}

      {/* Per-type account assignment */}
      {selectedType ? (
        <CariTypeDetailPanel
          cariType={selectedType}
          accountOptions={accountOptions}
          canEdit={canEdit}
          canDelete={canDelete}
          onAddCari={
            canAddCari(selectedType.code) && selectedType.isActive
              ? () =>
                  setCreateCariType({
                    code: selectedType.code,
                    label: cariTypeLabel(selectedType, i18n.language),
                  })
              : undefined
          }
        />
      ) : (
        !isLoading && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t('cariTypes.selectTypeHint')}
          </p>
        )
      )}

      {/* Create / edit dialog */}
      <CariTypeModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        cariType={editingType}
        accountOptions={accountOptions}
      />

      {/* Per-type "new cari" creation */}
      <CariCreateModals
        createType={createCariType?.code ?? null}
        typeLabel={createCariType?.label}
        onClose={() => {
          setCreateCariType(null);
          utils.tradingCariType.listDetailed.invalidate();
          utils.tradingCariType.carisWithAccounts.invalidate();
          utils.tradingCari.list.invalidate();
          utils.tradingCari.counts.invalidate();
        }}
      />

      {/* Delete confirmation */}
      <ConfirmationDialog
        open={!!typeToDelete}
        onOpenChange={(open) => {
          if (!open) setTypeToDelete(null);
        }}
        title={tCommon('delete')}
        description={t('cariTypes.deleteConfirm', { name: typeToDelete?.name ?? '' })}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={() => {
          if (typeToDelete) deleteMutation.mutate({ id: typeToDelete.id });
        }}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
